
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { GitBranch, ArrowRight, ArrowLeft, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MessageType, TopicType } from '@/types/assistant';

interface ConversationBranch {
  id: string;
  name: string;
  messages: MessageType[];
  createdAt: Date;
}

interface ConversationBranchingProps {
  currentBranch: string;
  branches: ConversationBranch[];
  topic: TopicType;
  onSwitchBranch: (branchId: string) => void;
  onCreateBranch: (fromMessageId?: string) => void;
}

export const ConversationBranching: React.FC<ConversationBranchingProps> = ({
  currentBranch,
  branches,
  topic,
  onSwitchBranch,
  onCreateBranch
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getTopicColor = () => {
    switch (topic) {
      case 'guide': return 'border-botscheme-royal-purple/30 text-botscheme-royal-purple';
      case 'performance': return 'border-botscheme-ocean-blue/30 text-botscheme-ocean-blue';
      case 'trading_strategy': return 'border-botscheme-emerald-green/30 text-botscheme-emerald-green';
      case 'advisor': return 'border-botscheme-amber-gold/30 text-botscheme-amber-gold';
      default: return 'border-primary/30 text-primary';
    }
  };

  const topicColorClass = getTopicColor();
  const currentBranchData = branches.find(b => b.id === currentBranch);

  if (branches.length <= 1) {
    return null;
  }

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        className={cn(
          "h-8 px-3 rounded-full transition-all duration-200",
          topicColorClass,
          "bg-background/60 backdrop-blur-sm",
          "hover:bg-white/10"
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <GitBranch className="h-3.5 w-3.5 mr-2" />
        <span className="text-xs font-medium">
          {currentBranchData?.name || 'Main'}
        </span>
        <span className="ml-2 text-xs opacity-60">
          ({branches.length})
        </span>
      </Button>

      <AnimatePresence>
        {isExpanded && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsExpanded(false)}
            />
            
            {/* Branch selector */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 top-full mt-2 z-50 min-w-80"
            >
              <div className="bg-background/95 backdrop-blur-md border border-white/20 rounded-xl shadow-xl p-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold">Conversation Branches</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 rounded-full"
                    onClick={() => {
                      onCreateBranch();
                      setIsExpanded(false);
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {branches.map((branch, index) => (
                    <motion.button
                      key={branch.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={cn(
                        "w-full flex items-center justify-between p-3 rounded-lg",
                        "hover:bg-white/10 transition-colors duration-200",
                        "text-left",
                        currentBranch === branch.id && "bg-white/10 ring-1 ring-white/20"
                      )}
                      onClick={() => {
                        onSwitchBranch(branch.id);
                        setIsExpanded(false);
                      }}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <GitBranch className="h-3.5 w-3.5 opacity-60" />
                          <span className="font-medium text-sm">{branch.name}</span>
                          {currentBranch === branch.id && (
                            <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 ml-5">
                          {branch.messages.length} messages • {' '}
                          {new Date(branch.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      
                      {currentBranch !== branch.id && (
                        <ArrowRight className="h-3.5 w-3.5 opacity-40" />
                      )}
                    </motion.button>
                  ))}
                </div>
                
                <div className="border-t border-white/10 mt-3 pt-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start h-8 text-xs"
                    onClick={() => {
                      onCreateBranch();
                      setIsExpanded(false);
                    }}
                  >
                    <Plus className="h-3.5 w-3.5 mr-2" />
                    Create new branch
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
